export const JSON_HEADERS = { "content-type": "application/json" } as const;

/** The API's `{ error }` message, or a status line when there is none. */
export async function responseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : `Request failed (${res.status})`;
}

/**
 * Alert text for a thrown error: `"<label>: <message>"` for an Error, so an
 * operator can tell a network drop from a parse failure, else `"<label>."`.
 */
export function describeError(err: unknown, label: string): string {
  return err instanceof Error && err.message ? `${label}: ${err.message}` : `${label}.`;
}

interface MutationHandlers {
  onError: (message: string | null) => void;
  /** Runs after a 2xx response (state updates, refetches). */
  onSuccess: () => Promise<void> | void;
}

/** Label for a failure after the write committed (the follow-up refetch). */
export const REFRESH_FAILED_LABEL = "Saved, but refreshing failed";

/**
 * One admin mutation in two phases. Request phase: clear the alert, send the
 * request, and report a non-2xx body or a thrown fetch under `failureLabel`.
 * Success phase: run `onSuccess`; the write has committed, so an error there
 * is reported as a refresh failure, never as the mutation failing. Resolves
 * true when the write committed.
 */
export async function runMutation(
  request: () => Promise<Response>,
  failureLabel: string,
  { onError, onSuccess }: MutationHandlers,
): Promise<boolean> {
  onError(null);
  try {
    const res = await request();
    if (!res.ok) {
      onError(await responseError(res));
      return false;
    }
  } catch (err) {
    onError(describeError(err, failureLabel));
    return false;
  }
  try {
    await onSuccess();
  } catch (err) {
    onError(describeError(err, REFRESH_FAILED_LABEL));
  }
  return true;
}
